import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PasswordField from '../components/shell/PasswordField';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { GraduationCap, Buildings, User, SpinnerGap, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { API_URL } from '../config/api';
import axios from 'axios';
import { BRAND_NAME } from '../brand';
import { InstallAppCard } from '../components/pwa/InstallApp';
import { takeSignOutReason, signedOutMessage } from '../components/security/AwayGuard';

export default function TenantLoginPage({ subdomainOverride }) {
  const params = useParams();
  const subdomain = subdomainOverride || params.subdomain;
  const [tenant, setTenant] = useState(null);
  const [loadingTenant, setLoadingTenant] = useState(true);
  const [tenantError, setTenantError] = useState(null);
  const [activeTab, setActiveTab] = useState('login');
  
  // Login form state
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Register form state
  const [registerForm, setRegisterForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    guardian_name: '',
    student_level: '',
    school_class: '',
    school_name: '',
    school_board: '',
    college_year: '',
    college_name: '',
    professional_course: '',
    professional_level: '',
    competitive_exam: ''
  });
  const [registerLoading, setRegisterLoading] = useState(false);
  
  const { login, user, isAuthenticated, logout } = useAuth();
  // Read once: being signed out for leaving the screen is confusing without a reason.
  const [signedOutNote] = useState(() => signedOutMessage(takeSignOutReason()));
  const navigate = useNavigate();

  // Someone already signed in has no business on a login page — send them to their
  // dashboard. The exception is a super admin, who reaches a tenant portal deliberately
  // (the "open portal" link on the tenant list): bouncing them back to /super-admin made
  // that link do nothing. They see the portal, with a banner saying whose it is.
  const isPlatformAdminVisiting = isAuthenticated && user?.role === 'super_admin';
  useEffect(() => {
    if (isAuthenticated && user && user.role !== 'super_admin') {
      const routes = {
        tenant_admin: '/admin',
        instructor: '/instructor',
        parent: '/diary',
        learner: '/learn'
      };
      navigate(routes[user.role] || '/');
    }
  }, [isAuthenticated, user, navigate]);

  // Fetch tenant info on mount
  useEffect(() => {
    const fetchTenant = async () => {
      try {
        const response = await axios.get(`${API_URL}/tenants/subdomain/${subdomain}`);
        setTenant(response.data);
      } catch (error) {
        setTenantError('Organization not found. Please check the URL.');
      } finally {
        setLoadingTenant(false);
      }
    };
    fetchTenant();
  }, [subdomain]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);

    try {
      // Pass tenant data to login function to store subdomain/custom_domain
      const user = await login(loginForm.email, loginForm.password, tenant.id, tenant);
      
      // Save tenant subdomain for logout redirect (backwards compatibility)
      localStorage.setItem('lms_tenant_subdomain', subdomain);
      
      toast.success(`Welcome back, ${user.first_name}!`);
      
      // Redirect based on role
      const routes = {
        tenant_admin: '/admin',
        instructor: '/instructor',
        parent: '/diary',
        learner: '/learn'
      };
      navigate(routes[user.role] || '/learn');
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed. Please check your credentials.';
      toast.error(message);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegisterLoading(true);

    try {
      await axios.post(`${API_URL}/public/tenant/${subdomain}/register-learner`, registerForm);
      toast.success('Account created! You can now sign in.');
      setActiveTab('login');
      setLoginForm({ email: registerForm.email, password: '' });
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setRegisterLoading(false);
    }
  };

  // Loading state
  if (loadingTenant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <SpinnerGap className="w-10 h-10 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">Loading organization...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (tenantError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-8">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <Warning className="w-10 h-10 text-red-600" weight="fill" />
            </div>
            <CardTitle className="font-heading text-2xl text-red-700">Organization Not Found</CardTitle>
            <CardDescription>{tenantError}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button 
              className="w-full" 
              onClick={() => navigate('/login')}
              data-testid="main-login-btn"
            >
              Go to Main Login
            </Button>
            <Button 
              variant="outline"
              className="w-full" 
              onClick={() => navigate('/register-tenant')}
              data-testid="register-org-btn"
            >
              Register New Organization
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const allowLearnerRegistration = tenant?.settings?.allow_learner_registration !== false;

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Form */}
      <div className="flex items-center justify-center p-8 bg-background overflow-y-auto">
        <div className="w-full max-w-md space-y-6">
          {/* Tenant Header */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Buildings className="w-6 h-6 text-primary-foreground" weight="duotone" />
            </div>
            <div>
              <span className="font-heading font-bold text-xl">{tenant.name}</span>
              <p className="text-xs text-muted-foreground">Learning Portal</p>
            </div>
          </div>

          {signedOutNote && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-[12px] text-amber-900"
              data-testid="signed-out-reason">
              {signedOutNote}
            </div>
          )}
          {isPlatformAdminVisiting && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-[12px] text-amber-900" data-testid="super-admin-portal-notice">
              You are signed in as the platform admin and viewing <strong>{tenant.name}</strong>'s portal.
              Signing in below replaces your session with that account.{' '}
              <button type="button" className="font-medium underline" onClick={() => navigate('/super-admin')}>
                Back to the platform
              </button>
            </div>
          )}
          <div className="mb-4"><InstallAppCard orgName={tenant.name} /></div>
          <Card className="border-0 shadow-none bg-transparent">
            <CardHeader className="px-0 pb-4">
              <CardTitle className="font-heading text-2xl">Welcome to {tenant.name}</CardTitle>
              <CardDescription>Sign in or create an account to access your courses</CardDescription>
            </CardHeader>
            <CardContent className="px-0">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
                  <TabsTrigger 
                    value="register" 
                    disabled={!allowLearnerRegistration}
                    data-testid="register-tab"
                  >
                    Create Account
                  </TabsTrigger>
                </TabsList>

                {/* Login Tab */}
                <TabsContent value="login" className="pt-4 space-y-4">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="field">
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="you@company.com"
                        value={loginForm.email}
                        onChange={(e) => setLoginForm(prev => ({ ...prev, email: e.target.value }))}
                        required
                        data-testid="login-email-input"
                      />
                    </div>

                    <PasswordField
  id="login-password"
  label="Password"
  value={loginForm.password}
  onChange={(v) => setLoginForm(prev => ({ ...prev, password: v }))}
  placeholder="Enter your password"
  required
  minLength={1}
  autoComplete="current-password"
  testid="login-password-input"
/>

                    <Button type="submit" className="w-full" disabled={loginLoading} data-testid="login-submit">
                      {loginLoading ? 'Signing in...' : 'Sign In'}
                    </Button>
                  </form>
                </TabsContent>

                {/* Register Tab */}
                <TabsContent value="register" className="pt-4">
                  {allowLearnerRegistration ? (
                    <form onSubmit={handleRegister} className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label htmlFor="register-first-name" className="text-xs">First Name *</Label>
                          <Input
                            id="register-first-name"
                            type="text"
                            placeholder="John"
                            value={registerForm.first_name}
                            onChange={(e) => setRegisterForm(prev => ({ ...prev, first_name: e.target.value }))}
                            required
                            className="h-9"
                            data-testid="register-first-name-input"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="register-last-name" className="text-xs">Last Name *</Label>
                          <Input
                            id="register-last-name"
                            type="text"
                            placeholder="Doe"
                            value={registerForm.last_name}
                            onChange={(e) => setRegisterForm(prev => ({ ...prev, last_name: e.target.value }))}
                            required
                            className="h-9"
                            data-testid="register-last-name-input"
                          />
                        </div>
                      </div>

                      <div className="space-y-1">
                        <Label htmlFor="register-guardian" className="text-xs">Guardian Name *</Label>
                        <Input
                          id="register-guardian"
                          type="text"
                          placeholder="Parent/Guardian name"
                          value={registerForm.guardian_name}
                          onChange={(e) => setRegisterForm(prev => ({ ...prev, guardian_name: e.target.value }))}
                          required
                          className="h-9"
                          data-testid="register-guardian-input"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label htmlFor="register-email" className="text-xs">Email</Label>
                          <Input
                            id="register-email"
                            type="email"
                            placeholder="you@example.com"
                            value={registerForm.email}
                            onChange={(e) => setRegisterForm(prev => ({ ...prev, email: e.target.value }))}
                            className="h-9"
                            data-testid="register-email-input"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="register-phone" className="text-xs">Phone Number</Label>
                          <Input
                            id="register-phone"
                            type="tel"
                            placeholder="+91 9876543210"
                            value={registerForm.phone}
                            onChange={(e) => setRegisterForm(prev => ({ ...prev, phone: e.target.value }))}
                            className="h-9"
                            data-testid="register-phone-input"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground -mt-1">Email or Phone is required</p>

                      <PasswordField
  id="register-password"
  label="Password"
  value={registerForm.password}
  onChange={(v) => setRegisterForm(prev => ({ ...prev, password: v }))}
  placeholder="Min 6 characters"
  required
  minLength={6}
  testid="register-password-input"
  generate
/>

                      <div className="space-y-1">
                        <Label htmlFor="register-level" className="text-xs">Student Level *</Label>
                        <select
                          id="register-level"
                          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                          value={registerForm.student_level}
                          onChange={(e) => setRegisterForm(prev => ({ 
                            ...prev, 
                            student_level: e.target.value,
                            school_class: '', school_name: '', school_board: '',
                            college_year: '', college_name: '',
                            professional_course: '', professional_level: '',
                            competitive_exam: ''
                          }))}
                          required
                          data-testid="register-level-select"
                        >
                          <option value="">Select Level...</option>
                          <option value="school">School</option>
                          <option value="college">College</option>
                          <option value="competitive">Competitive</option>
                          <option value="professional">Professional</option>
                        </select>
                      </div>

                      {/* School Fields */}
                      {registerForm.student_level === 'school' && (
                        <div className="space-y-3 p-3 border rounded-lg bg-muted/30">
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Class *</Label>
                              <select
                                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                                value={registerForm.school_class}
                                onChange={(e) => setRegisterForm(prev => ({ ...prev, school_class: e.target.value }))}
                                required
                                data-testid="register-school-class"
                              >
                                <option value="">Select Class</option>
                                {Array.from({length: 12}, (_, i) => i + 1).map(n => (
                                  <option key={n} value={String(n)}>Class {n}</option>
                                ))}
                              </select>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Board *</Label>
                              <select
                                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                                value={registerForm.school_board}
                                onChange={(e) => setRegisterForm(prev => ({ ...prev, school_board: e.target.value }))}
                                required
                                data-testid="register-school-board"
                              >
                                <option value="">Select Board</option>
                                <option value="CBSE">CBSE</option>
                                <option value="SSC">SSC</option>
                                <option value="IGSE">IGSE</option>
                                <option value="ICSE">ICSE</option>
                                <option value="Others">Others</option>
                              </select>
                            </div>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">School Name *</Label>
                            <Input
                              placeholder="Enter school name"
                              value={registerForm.school_name}
                              onChange={(e) => setRegisterForm(prev => ({ ...prev, school_name: e.target.value }))}
                              required
                              className="h-9"
                              data-testid="register-school-name"
                            />
                          </div>
                        </div>
                      )}

                      {/* College Fields */}
                      {registerForm.student_level === 'college' && (
                        <div className="space-y-3 p-3 border rounded-lg bg-muted/30">
                          <div className="space-y-1">
                            <Label className="text-xs">Year *</Label>
                            <select
                              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                              value={registerForm.college_year}
                              onChange={(e) => setRegisterForm(prev => ({ ...prev, college_year: e.target.value }))}
                              required
                              data-testid="register-college-year"
                            >
                              <option value="">Select Year</option>
                              <option value="Inter 1st Year">Inter 1st Year</option>
                              <option value="Inter 2nd Year">Inter 2nd Year</option>
                            </select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">College Name *</Label>
                            <Input
                              placeholder="Enter college name"
                              value={registerForm.college_name}
                              onChange={(e) => setRegisterForm(prev => ({ ...prev, college_name: e.target.value }))}
                              required
                              className="h-9"
                              data-testid="register-college-name"
                            />
                          </div>
                        </div>
                      )}

                      {/* Competitive Fields */}
                      {registerForm.student_level === 'competitive' && (
                        <div className="space-y-3 p-3 border rounded-lg bg-muted/30">
                          <div className="space-y-1">
                            <Label className="text-xs">Exam Type *</Label>
                            <select
                              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                              value={registerForm.competitive_exam}
                              onChange={(e) => setRegisterForm(prev => ({ ...prev, competitive_exam: e.target.value }))}
                              required
                              data-testid="register-competitive-exam"
                            >
                              <option value="">Select Exam</option>
                              <option value="EAMCET">EAMCET</option>
                              <option value="MAINS">MAINS</option>
                              <option value="ADVANCED">ADVANCED</option>
                              <option value="NEET">NEET</option>
                            </select>
                          </div>
                        </div>
                      )}

                      {/* Professional Fields */}
                      {registerForm.student_level === 'professional' && (
                        <div className="space-y-3 p-3 border rounded-lg bg-muted/30">
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Course *</Label>
                              <select
                                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                                value={registerForm.professional_course}
                                onChange={(e) => setRegisterForm(prev => ({ ...prev, professional_course: e.target.value }))}
                                required
                                data-testid="register-prof-course"
                              >
                                <option value="">Select Course</option>
                                <option value="CA">CA</option>
                                <option value="CMA">CMA</option>
                                <option value="CS">CS</option>
                                <option value="US CMA">US CMA</option>
                                <option value="Others">Others</option>
                              </select>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Level *</Label>
                              <select
                                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                                value={registerForm.professional_level}
                                onChange={(e) => setRegisterForm(prev => ({ ...prev, professional_level: e.target.value }))}
                                required
                                data-testid="register-prof-level"
                              >
                                <option value="">Select Level</option>
                                <option value="Foundation">Foundation</option>
                                <option value="Inter">Inter</option>
                                <option value="Final">Final</option>
                                <option value="Part 1">Part 1</option>
                                <option value="Part 2">Part 2</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      )}

                      <Button type="submit" className="w-full" disabled={registerLoading} data-testid="register-submit">
                        {registerLoading ? 'Creating Account...' : 'Create Account'}
                      </Button>
                    </form>
                  ) : (
                    <div className="text-center py-8 space-y-4">
                      <User className="w-12 h-12 mx-auto text-muted-foreground" />
                      <div>
                        <p className="font-medium">Registration Disabled</p>
                        <p className="text-sm text-muted-foreground">
                          Self-registration is not enabled for this organization. 
                          Please contact your administrator.
                        </p>
                      </div>
                    </div>
                  )}
                </TabsContent>
              </Tabs>

              <div className="mt-6 pt-6 border-t">
                <p className="text-center text-sm text-muted-foreground">
                  Looking for a different organization?{' '}
                  <Link to="/login" className="text-primary hover:underline" data-testid="main-login-link">
                    Main Login
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right side - Branding */}
      <div className="hidden lg:block relative">
        <div className="absolute inset-0 bg-primary/5" />
        <img
          src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHx0ZWFtJTIwbGVhcm5pbmd8ZW58MHx8fHwxNzc0MzQ1MzAwfDA&ixlib=rb-4.1.0&q=85"
          alt="Team learning"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <GraduationCap className="w-7 h-7" weight="duotone" />
            </div>
            <span className="font-heading font-bold text-2xl">{BRAND_NAME}</span>
          </div>
          <h2 className="font-heading text-3xl font-bold mb-4">
            Your Learning Journey Starts Here
          </h2>
          <p className="text-lg opacity-90">
            Access world-class training content, track your progress, and achieve your learning goals.
          </p>
        </div>
      </div>
    </div>
  );
}
